import React from 'react';
import { 
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle } from 'lucide-react';

// Define a proper interface for the deletion impact report
interface DeletionImpactReport {
  primaryDocument: {
    type: string;
    id: string;
    name: string;
  };
  dependentDocuments: {
    [collectionName: string]: {
      count: number;
      sampleItems?: Array<{id: string, name: string}>;
    }
  };
  totalImpactedDocuments: number;
}

// Unified props interface for the consolidated dialog
interface DeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  entityName: string;
  assessmentState: 'loading' | 'completed' | 'error';
  impactReport: DeletionImpactReport | null;
  deletionState: 'idle' | 'loading' | 'error';
  error?: string | null;
  onConfirm: () => Promise<void>;
}

export const DeleteDialog: React.FC<DeleteDialogProps> = ({
  isOpen,
  onClose,
  entityType,
  entityName,
  assessmentState,
  impactReport,
  deletionState,
  error,
  onConfirm
}) => {
  // Enhanced cleanup on dialog exit
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };
  
  // Format impact summary text
  const getImpactSummary = () => {
    if (!impactReport || !impactReport.dependentDocuments) {
      return 'No dependent documents found.';
    }
    
    const items: string[] = [];
    
    // Add summary for each dependent document type
    Object.entries(impactReport.dependentDocuments).forEach(([collectionName, details]) => {
      if (details.count > 0) {
        const formattedCollectionName = collectionName
          .replace(/([A-Z])/g, ' $1') // Add space before capital letters
          .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
        
        // Special case for custom maps when deleting a location - they're updated, not deleted
        if (collectionName === 'customMaps' && entityType.toLowerCase() === 'location') {
          items.push(`${details.count} Custom Map Entries`);
        } else {
          items.push(`${details.count} ${formattedCollectionName}`);
        }
      }
    });
    
    return items.length > 0 ? items.join('\n') : 'No dependent documents found.';
  };

  const renderDialogContent = () => {
    // If we're actively deleting, show a clear deletion in progress state
    if (deletionState === 'loading') {
      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-destructive" />
          <span className="text-center font-medium text-lg">Deleting {entityName}...</span>
          <span className="text-center text-muted-foreground text-sm">
            Please wait while the deletion completes
          </span>
        </div>
      );
    }
    
    if (assessmentState === 'loading') {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          <span className="ml-2">Assessing impact...</span>
        </div>
      );
    }

    if (assessmentState === 'error') {
      return (
        <div className="space-y-2">
          <div className="flex items-center text-red-500">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>Error assessing deletion impact.</span>
          </div>
          {error && (
            <div className="text-sm bg-red-50 p-3 rounded text-red-600">
              {error}
            </div>
          )}
        </div>
      );
    }

    // Assessment completed - show impact if any
    if (impactReport && impactReport.totalImpactedDocuments > 0) {
      // Check if we're deleting a location that's referenced in custom maps
      const hasCustomMaps = entityType.toLowerCase() === 'location' && 
        impactReport.dependentDocuments?.customMaps?.count > 0;
      
      // Calculate the actual deletion count by subtracting customMaps count when deleting a location
      const customMapsCount = hasCustomMaps ? impactReport.dependentDocuments.customMaps.count : 0;
      const actualDeletionCount = impactReport.totalImpactedDocuments - (hasCustomMaps ? customMapsCount : 0);

      return (
        <div className="space-y-2">
          <span className="block">
            This action will also delete <strong>{actualDeletionCount - 1} additional</strong> items 
            from the database{hasCustomMaps ? ` and update references in ${customMapsCount} custom map${customMapsCount > 1 ? 's' : ''}` : ''}:
          </span>
          <pre className="whitespace-pre-line text-sm bg-gray-100 p-3 rounded block mt-2">
            {getImpactSummary()}
          </pre>
          <span className="text-red-500 font-semibold block mt-2">
            Do you want to proceed?
          </span>
        </div>
      );
    }

    // No impact or simple confirmation
    return (
      <div className="space-y-2">
        <p>
          Are you sure you want to delete <strong>{entityName}</strong>?
        </p>
        <span className="text-red-500 font-semibold block mt-2">
          This action cannot be undone.
        </span>
      </div>
    );
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        {/* Use helper variables */}
        {(() => {
          const isDeleting = deletionState === 'loading';
          const isAssessing = assessmentState === 'loading';
          const hasError = assessmentState === 'error' || deletionState === 'error'; 
          const canShowDescription = !isDeleting && !isAssessing && !hasError;
          
          return (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
                  {isDeleting ? `Deleting ${entityType}...` : `Delete ${entityType}`}
                </AlertDialogTitle>
                {canShowDescription && (
                  <AlertDialogDescription>
                    Are you sure you want to delete this {entityType}?
                  </AlertDialogDescription>
                )}
              </AlertDialogHeader>
                  
              {/* Move complex content outside AlertDialogDescription */}
              <div className="py-2">
                {renderDialogContent()}
                
                {deletionState === 'error' && error && (
                  <div className="text-red-500 mt-4 bg-red-50 p-2 rounded">
                    Error: {error}
                  </div>
                )}
              </div>
                  
              <AlertDialogFooter>
                {isDeleting ? (
                  // When deleting is in progress, just show "Deleting..." text
                  <span className="text-muted-foreground text-sm">
                    Please wait while the deletion completes...
                  </span>
                ) : (
                  // Normal buttons when not deleting
                  <>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <Button 
                      disabled={isAssessing || deletionState !== 'idle'} 
                      onClick={onConfirm}
                      className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                    >
                      Delete permanently
                    </Button>
                  </>
                )}
              </AlertDialogFooter>
            </>
          );
        })()}
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Keep the old components for backward compatibility but implement them using the new unified component
export const DeleteConfirmDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  entityName: string;
  isLoading: boolean;
  error?: string | null;
  onConfirm: () => Promise<void>;
}> = (props) => {
  return (
    <DeleteDialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      entityType={props.entityType}
      entityName={props.entityName}
      assessmentState="completed"
      impactReport={null}
      deletionState={props.isLoading ? 'loading' : 'idle'}
      error={props.error}
      onConfirm={props.onConfirm}
    />
  );
};

export const DeleteImpactDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  entityName: string;
  impactReport: DeletionImpactReport | null;
  isLoading: boolean;
  error?: string | null;
  onConfirm: () => Promise<void>;
}> = (props) => {
  return (
    <DeleteDialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      entityType={props.entityType}
      entityName={props.entityName}
      assessmentState={props.impactReport ? "completed" : "loading"}
      impactReport={props.impactReport}
      deletionState={props.isLoading ? 'loading' : 'idle'}
      error={props.error}
      onConfirm={props.onConfirm}
    />
  );
}; 